import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Image as ImageIcon, Eye, Download, ArrowLeft, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * DescargarGuiaMenu — modal para VER o DESCARGAR la guía de transporte.
 *
 * - "Ver guía": carga el archivo y lo muestra inline (imagen o PDF iframe).
 * - "Descargar PDF" / "Descargar JPG": descarga el archivo al dispositivo.
 *
 * Props:
 *   - pedidoId: id del pedido
 *   - nombreArchivo: nombre amistoso para el download (p. ej. "PED-001")
 *   - onClose: () => void
 *   - onDone: () => void (se dispara después de ver o descargar)
 */
export default function DescargarGuiaMenu({ pedidoId, nombreArchivo, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [viewer, setViewer] = useState(null); // { url, type, filename }
  const triggeredOnDone = useState({ current: false })[0];

  // Liberar objectURL al cerrar
  useEffect(() => {
    return () => {
      if (viewer?.url) {
        try { URL.revokeObjectURL(viewer.url); } catch (_) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer?.url]);

  const _fireOnDone = () => {
    if (!triggeredOnDone.current && onDone) {
      triggeredOnDone.current = true;
      onDone();
    }
  };

  const abrirVista = async () => {
    if (!pedidoId || loading) return;
    setLoading(true);
    try {
      const { data, headers } = await api.get(`/pedidos/${pedidoId}/guia`, {
        params: { format: "original" },
        responseType: "blob",
      });
      const ct = (headers["content-type"] || data?.type || "").toLowerCase();
      const isPdf = ct.includes("pdf");
      const mime = isPdf ? "application/pdf" : (ct || "image/jpeg");
      const blob = new Blob([data], { type: mime });
      const url = URL.createObjectURL(blob);
      const base = nombreArchivo || (pedidoId ? pedidoId.slice(0, 8) : "guia");
      setViewer({
        url,
        type: isPdf ? "pdf" : "image",
        filename: isPdf ? `guia-${base}.pdf` : `guia-${base}.jpg`,
      });
      _fireOnDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar la guía");
    } finally {
      setLoading(false);
    }
  };

  const descargar = async (format) => {
    if (!pedidoId || loading) return;
    setLoading(true);
    try {
      const { data, headers } = await api.get(`/pedidos/${pedidoId}/guia`, {
        params: { format },
        responseType: "blob",
      });
      const mime = format === "jpg"
        ? "image/jpeg"
        : (headers["content-type"] || "application/octet-stream");
      const url = URL.createObjectURL(new Blob([data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      const base = nombreArchivo || (pedidoId ? pedidoId.slice(0, 8) : "guia");
      a.download = format === "jpg" ? `guia-${base}.jpg` : `guia-${base}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      _fireOnDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo descargar la guía");
    } finally {
      setLoading(false);
    }
  };

  const abrirEnNuevaPestana = () => {
    if (!viewer?.url) return;
    try { window.open(viewer.url, "_blank", "noopener,noreferrer"); } catch (_) {}
  };

  // --- Vista: previsualización inline ---
  if (viewer) {
    return (
      <Dialog open={!!pedidoId} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent
          className="max-w-4xl w-[95vw] h-[90vh] p-0 flex flex-col overflow-hidden grid-cols-[minmax(0,1fr)]"
          data-testid="guia-visor-modal"
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/40 min-w-0">
            <button
              onClick={() => { try { URL.revokeObjectURL(viewer.url); } catch(_) {} setViewer(null); }}
              className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline shrink-0"
              data-testid="guia-visor-volver"
            >
              <ArrowLeft className="w-4 h-4" /> Opciones
            </button>
            <div className="text-xs text-muted-foreground truncate">{viewer.filename}</div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={abrirEnNuevaPestana}
                title="Abrir en pestaña nueva"
                className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-primary/10 text-primary"
                data-testid="guia-visor-nueva-pestana"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
              <button
                onClick={() => descargar(viewer.type === "pdf" ? "original" : "jpg")}
                title="Descargar"
                className="h-8 px-3 flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-60"
                disabled={loading}
                data-testid="guia-visor-descargar"
              >
                <Download className="w-3.5 h-3.5" /> Descargar
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 min-w-0 bg-slate-900/95 overflow-auto flex items-center justify-center">
            {viewer.type === "pdf" ? (
              <iframe
                src={viewer.url}
                title="Guía de transporte"
                className="w-full h-full border-0"
                data-testid="guia-visor-iframe"
              />
            ) : (
              <img
                src={viewer.url}
                alt="Guía de transporte"
                className="max-w-full max-h-full object-contain select-none"
                data-testid="guia-visor-img"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // --- Vista: menú de opciones ---
  return (
    <Dialog open={!!pedidoId} onOpenChange={(v) => { if (!v && !loading) onClose(); }}>
      <DialogContent className="max-w-md grid-cols-[minmax(0,1fr)]" data-testid="guia-formato-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Guía de transporte</DialogTitle>
          <p className="text-sm text-muted-foreground">Ver la guía adjunta o descargarla al dispositivo.</p>
        </DialogHeader>
        <div className="mt-2">
          <button
            type="button"
            disabled={loading}
            onClick={abrirVista}
            className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-primary bg-primary/5 hover:bg-primary/10 transition-all disabled:opacity-50 text-left"
            data-testid="guia-ver-btn"
          >
            {loading ? <Loader2 className="w-7 h-7 text-primary animate-spin shrink-0" /> : <Eye className="w-7 h-7 text-primary shrink-0" />}
            <div className="min-w-0">
              <div className="text-base font-bold text-primary">Ver guía</div>
              <div className="text-xs text-muted-foreground">Abre el archivo acá mismo, sin descargarlo</div>
            </div>
          </button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <button
            type="button"
            disabled={loading}
            onClick={() => descargar("original")}
            className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50"
            data-testid="guia-format-pdf"
          >
            <FileText className="w-7 h-7 text-primary" />
            <span className="text-sm font-semibold">Descargar PDF</span>
            <span className="text-[10px] text-muted-foreground text-center">Documento con formato original</span>
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => descargar("jpg")}
            className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50"
            data-testid="guia-format-jpg"
          >
            <ImageIcon className="w-7 h-7 text-primary" />
            <span className="text-sm font-semibold">Descargar JPG</span>
            <span className="text-[10px] text-muted-foreground text-center">Ideal para WhatsApp o galería</span>
          </button>
        </div>
        {loading && <div className="mt-3 text-center text-xs text-muted-foreground">Cargando…</div>}
      </DialogContent>
    </Dialog>
  );
}
